import type { FC } from "react";
import { lazy, Suspense, useMemo, useState } from "react";
import "../../common/table/data-table.css";
import "../../index.css";
import { publishManager } from "./publish-manager";
import { useSnapshot } from "valtio";
import { Tabs, TabsList, TabsTrigger } from "@renewmap/ui/tabs";
import {
  ColumnDiff,
  RowDiff,
  PublishableTypes,
  FieldConfigShort,
  type EntityTable,
} from "@renewmap/model";
import {
  ColDef,
  IRowNode,
  RowDataUpdatedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import {
  defaultCellStyle,
  defaultDiffCellRenderer,
  entityIdCellRenderer,
  geomAreaCellRenderer,
  geomCellStyle,
  getFieldValueString,
  pointCellRenderer,
  operationTypeCellRenderer,
  getRowOperationType,
  valueStringArrayCellRenderer,
} from "@/common/table/cell-renderers";
import { TableProperties } from "lucide-react";
import { selectedRowState, toRowWithGeom } from "./geom-info";

const LazyGrid = lazy(() => import("../../common/table/async-grid"));

// Extract searchable text from ColumnDiff for quick filter
// Returns both public and draft values (space-separated) so search works on either
const getter = (diff: ColumnDiff | undefined | null): string => {
  if (!diff) {
    return "";
  }

  const publicValue = getFieldValueString(diff.public);
  const draftValue = getFieldValueString(diff.draft);

  // Filter out null values and convert to strings
  const values = [publicValue, draftValue]
    .filter((v) => v !== null && v !== undefined)
    .map((v) => String(v));

  const uniqueValues = [...new Set(values)];

  // Unchanged: show the single value. Changed: show old → new.
  return uniqueValues.join(" → ");
};

// Sort by draft (new) value only, not the concatenated search text
const draftValueComparator = (
  a: ColumnDiff | undefined | null,
  b: ColumnDiff | undefined | null,
): number => {
  const aValue = String(getFieldValueString(a?.draft) ?? "");
  const bValue = String(getFieldValueString(b?.draft) ?? "");
  return aValue.localeCompare(bValue);
};

const USER_SELECTION_SOURCES = new Set<string>([
  "checkboxSelected",
  "uiSelectAll",
  "uiSelectAllFiltered",
  "uiSelectAllCurrentPage",
  "spaceKey",
]);

// Columns to always show regardless of whether they have a diff
const alwaysVisibleColumns = new Set<string>([
  "project_name",
  "technology",
  "project_status",
  "organisation_name",
  "name",
]);

// Columns to hide by default (shown only when "Show all columns" is enabled)
const alwaysHiddenColumns = new Set<string>([
  "id",
  "created",
  "updated",
  "published",
  "to_be_published",
]);

// Entity tables with geom_area/point/line columns for the geom box
const geomEntityTables = new Set<EntityTable>([
  "projects",
  "substations_v1",
  "infrastructure_v5",
]);

// Entity id columns per table.  These are rendered as badges with the entity name from entityRegistry
const entityIdColumnsPerTable: Partial<Record<PublishableTypes, Set<string>>> =
  {
    entity_links: new Set(["source_id", "target_id"]),
    ppa: new Set(["source_id", "target_id"]),
    time_series_v1: new Set(["project_id", "entity_id"]),
    field_values_v1: new Set(["entity_id"]),
    technical_plant: new Set(["project_id"]),
    technical_aemo: new Set(["project_id"]),
    technical_turbine_locations: new Set(["project_id"]),
    changelog: new Set(["entity_id", "project_id", "linked_entity_id"]),
  };

interface ChangedEntriesProps {
  quickSearchValue: string;
  showAllColumns: boolean;
}

export const ChangedEntries: FC<ChangedEntriesProps> = ({
  quickSearchValue,
  showAllColumns,
}) => {
  const [selectedTab, setSelectedTab] = useState<string>("projects");
  const { types, changedColumns, publishIds, entityRegistry, fieldConfigs } =
    useSnapshot(publishManager.publishState);
  const fieldConfigByKey = useMemo(
    () =>
      new Map(
        fieldConfigs.map((config) => [
          `${config.entity_type}::${config.field_name}`,
          config,
        ]),
      ) as Map<string, FieldConfigShort>,
    [fieldConfigs],
  );

  const selectedType = types[selectedTab as PublishableTypes];

  // Custom external filter for quick search
  // AG Grid's native quickFilterText wasn't working reliably with ColumnDiff objects
  const isExternalFilterPresent = (): boolean => {
    return Boolean(quickSearchValue && quickSearchValue.trim().length > 0);
  };

  const doesExternalFilterPass = (node: IRowNode | { data: unknown }) => {
    const searchTerm = quickSearchValue.toLowerCase();
    const rowData = node.data as RowDiff;

    // Access values from RowDiff structure
    const values = rowData?.values || {};

    // Search through all value fields
    for (const key in values) {
      const value = values[key];
      if (
        value &&
        typeof value === "object" &&
        "draft" in value &&
        "public" in value
      ) {
        // It's a ColumnDiff object
        const searchText = getter(value).toLowerCase();
        if (searchText.includes(searchTerm)) {
          return true;
        }
      }
    }

    return false;
  };

  // Helper function to check if a table has any matching rows for the search term
  const tableHasMatchingRows = (tableType: PublishableTypes): boolean => {
    if (!quickSearchValue || quickSearchValue.trim().length === 0) {
      return true; // No filter, show all tabs
    }

    const entries = types[tableType]?.entries || [];

    // Check if any entry would pass the external filter
    return entries.some((entry) => doesExternalFilterPass({ data: entry }));
  };

  // Auto-switch to first visible tab if current tab is filtered out by search
  const visibleTabs = Object.keys(types).filter(
    (type) =>
      types[type as PublishableTypes].entries.length > 0 &&
      tableHasMatchingRows(type as PublishableTypes),
  );

  // If current tab is not visible, switch to first visible tab
  if (visibleTabs.length > 0 && !visibleTabs.includes(selectedTab)) {
    setSelectedTab(visibleTabs[0]);
  }

  const selectedTable = selectedTab as PublishableTypes;

  // Memoized so publishIds changes don't produce a new columnDefs reference —
  // AG Grid resets active filters whenever it receives new column definitions.
  const columnDefs = useMemo(() => {
    const columnIds = changedColumns[selectedTable];
    let columnKeys: string[] = [];

    if (showAllColumns) {
      columnKeys = [...(selectedType?.columns ?? [])];
    } else {
      columnKeys = selectedType?.columns.filter(
        (col) =>
          !alwaysHiddenColumns.has(col) &&
          (alwaysVisibleColumns.has(col) ||
            col.startsWith("v_") ||
            columnIds.has(col)),
      );
    }

    if (columnKeys) {
      const nameCol = columnKeys.filter((col) => alwaysVisibleColumns.has(col));
      const lookupColumns = columnKeys.filter((col) => col.startsWith("v_"));
      const otherColumns = columnKeys.filter(
        (col) => ![...nameCol, ...lookupColumns].includes(col),
      );
      columnKeys = [...nameCol, ...lookupColumns, ...otherColumns];
    }

    const operationTypeColumn: ColDef = {
      field: "operation_type",
      headerName: "Op",
      width: 80,
      pinned: "left",
      cellRenderer: operationTypeCellRenderer,
      sortable: false,
      filter: "agSetColumnFilter",
      filterValueGetter: (params) => getRowOperationType(params.data),
      suppressMovable: true,
    };

    const entityIdColumns = entityIdColumnsPerTable[selectedTable];

    const dataColumnDefs = columnKeys?.map((col) => {
      if (entityIdColumns?.has(col)) {
        return {
          field: col,
          headerName: col,
          type: "text",
          cellStyle: defaultCellStyle,
          cellRenderer: entityIdCellRenderer,
          cellRendererParams: { entityRegistry },
          valueGetter: (params) => params.data?.values?.[col],
          filterValueGetter: (params) => getter(params?.data?.values?.[col]),
          sortable: true,
          comparator: draftValueComparator,
          filter: "agSetColumnFilter",
          floatingFilter: false,
        } as ColDef;
      }

      switch (col) {
        case "geom_area":
          return {
            field: col,
            headerName: col,
            type: "object",
            valueGetter: (params) => params.data?.values?.[col],
            cellStyle: geomCellStyle,
            cellRenderer: geomAreaCellRenderer,
            sortable: false,
            filter: false,
          } as ColDef;
        case "point":
          return {
            field: col,
            headerName: col,
            type: "object",
            valueGetter: (params) => params.data?.values?.[col],
            cellStyle: geomCellStyle,
            cellRenderer: pointCellRenderer,
            sortable: false,
            filter: false,
          } as ColDef;
        case "to_be_published":
          return {
            field: col,
            headerName: col,
            type: "object",
            hide: true,
            filter: false,
          } as ColDef;
        case "value_string_array":
          return {
            field: col,
            headerName: col,
            type: "text",
            cellStyle: defaultCellStyle,
            cellRenderer: valueStringArrayCellRenderer,
            cellRendererParams: { entityRegistry, fieldConfigByKey },
            valueGetter: (params) => params.data?.values?.[col],
            filterValueGetter: (params) => getter(params?.data?.values?.[col]),
            sortable: true,
            comparator: draftValueComparator,
            filter: "agSetColumnFilter",
            floatingFilter: false,
          } as ColDef;
        default:
          return {
            field: col,
            headerName: col,
            type: "text",
            cellStyle: defaultCellStyle,
            cellRenderer: defaultDiffCellRenderer,
            valueGetter: (params) => params.data?.values?.[col],
            filterValueGetter: (params) => getter(params?.data?.values?.[col]),
            sortable: true,
            comparator: draftValueComparator,
            filter: "agSetColumnFilter",
            floatingFilter: false,
          } as ColDef;
      }
    });

    return [operationTypeColumn, ...(dataColumnDefs || [])];
  }, [
    selectedTable,
    showAllColumns,
    selectedType?.columns,
    changedColumns,
    entityRegistry,
    fieldConfigByKey,
  ]);

  return (
    <>
      <div className="flex gap-3">
        <Tabs onValueChange={setSelectedTab} value={selectedTab}>
          <TabsList className="h-auto min-h-12 w-full flex-wrap items-start px-1">
            {Object.keys(types)
              .filter(
                (type) =>
                  types[type as PublishableTypes].entries.length > 0 &&
                  tableHasMatchingRows(type as PublishableTypes),
              )
              .map((type) => {
                const entries = types[type as PublishableTypes]
                  .entries as RowDiff[];
                const selectedUpdates = entries.filter(
                  (e) =>
                    e.operation_type !== "delete" &&
                    e.operation_type !== "lead" &&
                    publishIds.has(e.id),
                ).length;
                const selectedDeletions = entries.filter(
                  (e) =>
                    (e.operation_type === "delete" ||
                      e.operation_type === "lead") &&
                    publishIds.has(e.id),
                ).length;
                return (
                  <TabsTrigger
                    key={type}
                    className="flex h-auto min-h-10 flex-wrap items-center gap-1 whitespace-normal py-2 text-base"
                    data-testid="overview-tab"
                    value={type}
                  >
                    <TableProperties className="h-4 w-4" />
                    <span className="text-base">{type}</span>
                    {selectedUpdates > 0 && (
                      <span className="ml-1 rounded-md bg-green-600 px-1.5 py-0.5 text-xs font-medium text-white">
                        {selectedUpdates}
                      </span>
                    )}
                    {selectedDeletions > 0 && (
                      <span className="ml-0.5 rounded-md bg-red-500 px-1.5 py-0.5 text-xs font-medium text-white">
                        {selectedDeletions}
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}
          </TabsList>
        </Tabs>
      </div>
      <div className="ag-grid-overrides h-[45vh] w-full overflow-hidden">
        <Suspense fallback={null}>
          <LazyGrid
            loading={false}
            animateRows={false}
            rowSelection={{
              mode: "multiRow",
              headerCheckbox: true,
              selectAll: "filtered",
              enableClickSelection: false,
            }}
            selectionColumnDef={{
              pinned: "left",
              width: 40,
              sortable: false,
              resizable: false,
            }}
            enableCellTextSelection={true}
            groupHeaderHeight={0}
            getRowId={(params) => (params.data as RowDiff).id}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            columnDefs={columnDefs}
            rowData={(selectedType?.entries as unknown[]) ?? []}
            onSelectionChanged={(event: SelectionChangedEvent) => {
              if (!USER_SELECTION_SOURCES.has(event.source)) {
                return;
              }
              const selected: string[] = [];
              const deselected: string[] = [];
              event.api.forEachNode((node) => {
                const id = (node.data as RowDiff).id;
                (node.isSelected() ? selected : deselected).push(id);
              });
              publishManager.setPublishIds(selected, deselected);
            }}
            onRowDataUpdated={(event: RowDataUpdatedEvent) => {
              // Restore this tab's selection from publishIds (all selected by default).
              event.api.forEachNode((node) =>
                node.setSelected(
                  publishManager.publishState.publishIds.has(
                    (node.data as RowDiff).id,
                  ),
                ),
              );
            }}
            onRowClicked={(event) => {
              if (geomEntityTables.has(selectedTab as EntityTable)) {
                const rowDiff = event.data as RowDiff;
                selectedRowState.selectedRow = rowDiff
                  ? toRowWithGeom(rowDiff)
                  : null;
                selectedRowState.type = selectedTab as EntityTable;
                selectedRowState.changelogPoint = null;
                selectedRowState.changelogZoom = null;
              }
            }}
          />
        </Suspense>
      </div>
    </>
  );
};
